import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Instagram, Send, MessageCircle } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = '+91 9986345269';
const PHONE_RAW = '919986345269';
const EMAIL = 'sanjay@mindstromproductions.com';
const INSTAGRAM = 'mind.stormproductions';

const EVENT_TYPES = [
    'Corporate Event',
    'Product Launch',
    'Annual Day',
    'Conference',
    'Entertainment Show',
    'Destination / MICE',
    'Other',
];

export const Contact = () => {
    const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', event_type: '', message: '' });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please fill all required fields');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success('Enquiry sent. Our team will reach out shortly.');
            setForm({ name: '', email: '', phone: '', company: '', event_type: '', message: '' });
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong. Please try again or WhatsApp us.');
        } finally {
            setLoading(false);
        }
    };

    const waMessage = encodeURIComponent("Hi Mindstorm, I'd like to discuss an upcoming event.");
    const waLink = `https://wa.me/${PHONE_RAW}?text=${waMessage}`;

    return (
        <section id="contact" data-testid={CONTACT.root} className="relative py-24 sm:py-32 bg-black overflow-hidden">
            <div className="container-lux">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-8 h-px bg-gold" />
                            <span className="eyebrow">Contact</span>
                            <span className="text-white/30 text-[10px] tracking-[0.3em]">07</span>
                        </div>
                        <h2 className="font-display font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
                            Let&apos;s create
                            <br />
                            something <span className="italic text-gold font-extralight">extraordinary</span>
                            <br />
                            together.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <p className="text-white/60 text-base sm:text-lg leading-relaxed font-light">
                            Tell us about your event, and our team will craft an experience aligned with your vision and business objectives.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Form */}
                    <motion.form
                        onSubmit={submit}
                        data-testid={CONTACT.form}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7 bg-neutral-950 border border-white/10 p-8 sm:p-10 space-y-6"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <Field label="Full Name *" testId={CONTACT.name} value={form.name} onChange={update('name')} required />
                            <Field label="Email *" type="email" testId={CONTACT.email} value={form.email} onChange={update('email')} required />
                            <Field label="Phone" testId={CONTACT.phone} value={form.phone} onChange={update('phone')} />
                            <Field label="Company" testId={CONTACT.company} value={form.company} onChange={update('company')} />
                        </div>

                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Event Type</label>
                            <select
                                data-testid={CONTACT.eventType}
                                value={form.event_type}
                                onChange={update('event_type')}
                                className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-gold transition-colors appearance-none cursor-pointer"
                            >
                                <option value="" className="bg-neutral-900">Select an event type</option>
                                {EVENT_TYPES.map(t => <option key={t} value={t} className="bg-neutral-900">{t}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Message *</label>
                            <textarea
                                data-testid={CONTACT.message}
                                value={form.message}
                                onChange={update('message')}
                                required
                                rows={5}
                                placeholder="Tell us about your event, audience size, timing, and goals..."
                                className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 focus:outline-none focus:border-gold transition-colors resize-none"
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 pt-4">
                            <button
                                type="submit"
                                data-testid={CONTACT.submit}
                                disabled={loading}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Sending…' : 'Send Enquiry'}
                                <Send size={13} />
                            </button>
                            <a
                                data-testid={CONTACT.whatsapp}
                                href={waLink}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-secondary"
                            >
                                <MessageCircle size={13} />
                                WhatsApp Us
                            </a>
                        </div>
                    </motion.form>

                    {/* Contact info */}
                    <div className="lg:col-span-5 space-y-6">
                        <InfoBlock icon={Phone} label="Phone" testId={CONTACT.phoneLink} value={PHONE} href={`tel:${PHONE_RAW}`} />
                        <InfoBlock icon={Mail} label="Email" testId={CONTACT.emailLink} value={EMAIL} href={`mailto:${EMAIL}`} />
                        <InfoBlock icon={MapPin} label="Studio" value="Bengaluru, Karnataka, India" />
                        <InfoBlock icon={Instagram} label="Instagram" testId={CONTACT.instagram} value={`@${INSTAGRAM}`} href={`https://instagram.com/${INSTAGRAM}`} />

                        <div className="lux-card mt-8">
                            <div className="text-gold text-[10px] tracking-[0.3em] uppercase mb-4">Studio Hours</div>
                            <div className="text-white text-sm space-y-1 font-light">
                                <div className="flex justify-between border-b border-white/5 pb-2"><span className="text-white/60">Mon — Sat</span><span>10:00 — 20:00 IST</span></div>
                                <div className="flex justify-between pt-2"><span className="text-white/60">Sunday</span><span>By Appointment</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, testId, value, onChange, type = 'text', required }) => (
    <div>
        <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">{label}</label>
        <input
            data-testid={testId}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-gold transition-colors"
        />
    </div>
);

const InfoBlock = ({ icon: Icon, label, value, href, testId }) => {
    const inner = (
        <div className="group border-l-2 border-gold/40 hover:border-gold pl-6 py-3 transition-colors">
            <div className="flex items-center gap-3 text-gold text-[10px] tracking-[0.3em] uppercase mb-2">
                <Icon size={14} strokeWidth={1.6} />
                {label}
            </div>
            <div className="text-white text-lg font-light group-hover:text-gold transition-colors">{value}</div>
        </div>
    );
    return href ? (
        <a data-testid={testId} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="block">
            {inner}
        </a>
    ) : (
        <div>{inner}</div>
    );
};

export default Contact;
